import { AppCache } from '../../../shared/services/app-cache';

const inFlight = new Map();
// Tracks every key written via set() so deleteDatabase() can surgically remove
// only placement entries without affecting other AppCache consumers.
const placementKeys = new Set();

/**
 * Stringify object in stable order.
 * @param {object} obj 
 * @returns {string}
 */
function stableStringify(obj) {
  if (obj === undefined) return 'null';
  if (obj === null) return 'null';
  if (typeof obj !== 'object') return JSON.stringify(obj);
  if (Array.isArray(obj)) return '[' + obj.map(stableStringify).join(',') + ']';
  const keys = Object.keys(obj).sort();
  return '{' + keys.map(k => JSON.stringify(k) + ':' + stableStringify(obj[k])).join(',') + '}';
}

/**
 * PlacementCache module for caching placement data in memory and IndexedDB.
 * Delegates storage to the shared AppCache so both layers (in-memory + IndexedDB)
 * are managed in one place across the application.
 */
const PlacementCache = {
  /**
   * Get data.
   * @param {string} key key to fetch data.
   * @returns 
   */
  async get(key) {
    return AppCache.get(key);
  },
  /**
   * Set data.
   * @param {string} key 
   * @param {object} items 
   * @param {number} ttlMs 
   */
  async set(key, items, ttlMs = 60 * 60 * 1000) {
    placementKeys.add(key);
    await AppCache.set(key, items, ttlMs);
  },
  /**
   * Get in-flight promise.
   * @param {string} key 
   * @returns {Promise<any>}
   */
  getInFlight(key) {
    return inFlight.get(key);
  },
  /**
   * Set in-flight promise.
   * @param {string} key 
   * @param {Promise<any>} promise 
   */
  setInFlight(key, promise) {
    inFlight.set(key, promise);
  },
  /**
   * Clear in-flight promise.
   * @param {string} key 
   */
  clearInFlight(key) {
    inFlight.delete(key);
  },
  /**
   * Make cache key.
   * @param {String} campaignId - Campaign ID
   * @param {Object} filterObj - Filter object
   * @param {String} versionOneApi - API version
   * @returns {String} Generated cache key
   */
  makeKey(campaignId, filterObj, versionOneApi) {
    const copy = filterObj && typeof filterObj === 'object' ? { ...filterObj } : filterObj;
    if (copy && copy.page_no !== undefined) delete copy.page_no;
    if (copy && copy.page_size !== undefined) delete copy.page_size;
    return stableStringify({ campaignId, filterObj: copy, versionOneApi });
  },
  /**
   * Delete data by key.
   * @param {string} key
   */
  async delete(key) {
    try {
      placementKeys.delete(key);
      inFlight.delete(key);
      await AppCache.delete(key);
    } catch (err) {
      console.error('PlacementCache.delete error', err);
    }
  },
  /**
   * Clear all placement cache entries from memory and IndexedDB.
   * Only removes keys written by PlacementCache, leaving other AppCache
   * consumers (e.g. campaign prefetch) untouched.
   */
  async deleteDatabase() {
    try {
      inFlight.clear();
      AppCache.clearMemory();
      await Promise.all([...placementKeys].map(k => AppCache.delete(k).catch(() => {})));
      placementKeys.clear();
    } catch (err) {
      console.error('PlacementCache.deleteDatabase error', err);
    }
  }
};

export default PlacementCache;
